import React, { FC, useEffect, useState } from 'react';

import { Button, Space } from 'antd';
import { useNavigate } from 'react-router-dom';

import { UsersTable } from './components/tables/UsersTable';
import { showErrorMessage, showSuccessMessage } from '../../../../../../helpers/utils/ui';
import { billApi } from '../../../../api/endpoints/billApi';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';
import Loader from '../../../loader/Loader';

interface BillPeriod {
    months: number;
    title: string;
    sum: number;
    bonus_days: number;
    profit_percent: number;
}

/** Бонусные дни за оплату вперёд: период в месяцах => количество дней */
const PERIOD_BONUS_DAYS: Record<number, number> = { 1: 0, 3: 10, 6: 30, 12: 90 };

/** Общая форма кнопок выбора периода и кнопки пополнения — один ряд одинаковой высоты */
const periodButtonStyle: React.CSSProperties = {
    height: 64,
    padding: '8px 16px',
    textAlign: 'center',
    whiteSpace: 'normal',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
};

/**
 * Расчёт периодов из стоимости месяца.
 * Используется, пока бэкенд не отдал свой список: суммы те же,
 * поэтому кнопки видны сразу и не зависят от доступности метода.
 */
const buildPeriods = (monthlySum: number): BillPeriod[] => {
    if (!monthlySum || monthlySum <= 0) {
        return [];
    }

    return Object.keys(PERIOD_BONUS_DAYS).map((key) => {
        const months = parseInt(key);
        const bonusDays = PERIOD_BONUS_DAYS[months];

        return {
            months: months,
            title: months === 1 ? '30 дней' : `${months} мес.`,
            sum: Math.round(monthlySum * months),
            bonus_days: bonusDays,
            profit_percent: bonusDays > 0 ? Math.round(bonusDays / (30 * months) * 1000) / 10 : 0,
        };
    });
};

const BalanceTab: FC<{currency: string, onBillCreated?: () => void}> = ({ currency, onBillCreated }) => {
    const navigate = useNavigate();
    const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
    const [ pageData, setPageData ] = useState({
        currentTariff: '',
        currentBill: '',
        currentBonusBill: '',
        allBill: '',
        billLeft: ''
    });
    const [ periods, setPeriods ] = useState<BillPeriod[]>([]);
    const [ selectedMonths, setSelectedMonths ] = useState<number | null>(null);
    const [ creatingMonths, setCreatingMonths ] = useState<number | null>(null);
    const selectedPeriod = periods.find((period) => period.months === selectedMonths) || null;
    const [ pageLoading, setPageLoading ] = useState(false);
    const [ pageError, setPageError ] = useState(false);
    const fetchData = () => {
        setPageError(false);
        setPageLoading(true);

        billApi.getBalanceInfo(parseInt(selectedSchool?.id || '0'))
            .then((response) => {
                if (response.status === 200) {
                    setPageData({
                        currentTariff: response.data.tariff_name,
                        currentBill: response.data.main_balance,
                        currentBonusBill: response.data.bonus_balance,
                        allBill: response.data.overall_balance,
                        billLeft: response.data.details,
                    });

                    /** Кнопки периодов считаем сразу от стоимости месяца, дальше их уточнит бэкенд */
                    setPeriods((prev) => prev.length > 0 ? prev : buildPeriods(Number(response.data.tariff_30days)));
                } else {
                    setPageError(true);
                }
            })
            .catch((error) => {
                if (error?.response?.status !== 401) {
                    showErrorMessage('Ошибка при получении данных');
                }
                setPageError(true);
            })
            .finally(() => {
                setPageLoading(false);
            });
    };

    const fetchPeriods = () => {
        billApi.getBillPeriods(parseInt(selectedSchool?.id || '0'))
            .then((response) => {
                if (response.status === 200 && Array.isArray(response.data?.periods) && response.data.periods.length > 0) {
                    setPeriods(response.data.periods);
                }
            })
            .catch(() => {
                /** Метод недоступен — остаются кнопки, посчитанные от стоимости месяца */
            });
    };

    const createBill = () => {
        if (selectedPeriod === null) {
            return;
        }

        const months = selectedPeriod.months;
        setCreatingMonths(months);

        billApi.createBillByPeriod(parseInt(selectedSchool?.id || '0'), months)
            .then((response) => {
                const bonus = response.data?.bonus_days
                    ? `, бонусом ${response.data.bonus_days} дней после оплаты`
                    : '';

                showSuccessMessage(`Счёт №${response.data?.order_id} выставлен${bonus}`);
                /** Просим вкладку «Счета и акты» перезапросить список, чтобы новый счёт был виден сразу */
                onBillCreated?.();
                navigate('/users/billing?tab=item-2');
            })
            .catch((error) => {
                if (error?.response?.status !== 401) {
                    showErrorMessage('Не удалось выставить счёт, повторите попытку позже');
                }
            })
            .finally(() => {
                setCreatingMonths(null);
            });
    };

    useEffect(() => {
        fetchData();
        fetchPeriods();
    }, []);

    return pageLoading ? (
        <Loader loading={pageLoading} />
    ) : (
        <div
            style={{
                marginTop: 20,
                marginLeft: 5,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {
                pageError ?
                    <div>
                        Произошла ошибка при загрузке данных, повторите попытку позже.
                    </div>
                    :
                    <>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                            }}
                        >
                            <b>Тариф:&nbsp;</b>
                            <span>{pageData.currentTariff}</span>
                        </div>

                        <div style={{
                                marginTop: 25,
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                            }} >
                            <div>
                                <div>
                                    <b>Основной счет: </b>
                                    <span>
                                    {pageData.currentBill} {currency}
                                    </span>
                                </div>

                                <div>
                                    <b>Бонусный счет: </b>
                                    <span>
                                        {pageData.currentBonusBill} {currency}
                                    </span>
                                </div>
                            </div>

                            <div style={{ margin: '0 40px' }}>
                                <b>Итого баланс: </b>
                                <span style={{ fontSize: 18, fontWeight: 'bold' }}>{pageData.allBill} {currency}</span>
                            </div>

                            <div>
                                {pageData.billLeft}
                            </div>
                        </div>
                        <Space wrap align="start" size={12} style={{ marginTop:15 }}>
                            {
                                periods.map((period) => (
                                    <Button
                                        key={period.months}
                                        onClick={() => setSelectedMonths(period.months)}
                                        disabled={creatingMonths !== null}
                                        style={{
                                            ...periodButtonStyle,
                                            width:215,
                                            borderWidth:2,
                                            borderColor:'#ff4d4f',
                                            color:'rgba(0, 0, 0, 0.85)',
                                            background:selectedMonths === period.months ? '#fff1f0' : undefined,
                                            boxShadow:selectedMonths === period.months ? '0 0 0 2px rgba(255, 77, 79, .35)' : undefined,
                                        }}
                                    >
                                        <div style={{ fontWeight:700, fontSize:15 }}>
                                            {period.title} — {period.sum.toLocaleString('ru-RU')} {currency}
                                        </div>
                                        <div style={{ fontSize:12, color:period.bonus_days > 0 ? '#389e0d' : '#8c8c8c', minHeight:18 }}>
                                            {period.bonus_days > 0 ? `+${period.bonus_days} дней · выгода ${period.profit_percent}%` : 'без бонусов'}
                                        </div>
                                    </Button>
                                ))
                            }
                        </Space>

                        {
                            periods.length > 0 &&
                            <Space align="center" size={16} style={{ marginTop:14 }}>
                                <Button
                                    onClick={createBill}
                                    disabled={selectedPeriod === null}
                                    loading={creatingMonths !== null}
                                    danger
                                    type={'primary'}
                                    style={{ height:40, width:230, fontSize:16, fontWeight:600 }}
                                >
                                    Пополнить баланс
                                </Button>

                                <span style={{ color:'#8c8c8c' }}>
                                    {
                                        selectedPeriod === null
                                            ? 'Выберите период пополнения'
                                            : `Выбрано: ${selectedPeriod.title} — ${selectedPeriod.sum.toLocaleString('ru-RU')} ${currency}${selectedPeriod.bonus_days > 0 ? `, бонусом ${selectedPeriod.bonus_days} дней после оплаты` : ''}`
                                    }
                                </span>
                            </Space>
                        }
                        <h3 style={{ marginTop:'18px',fontWeight:700 }}>Важно!</h3>
                        <p style={{ fontWeight:500 }}>
                            Стоимость сервиса зависит от количество активных пользователей в течение 30 дней.<br />
                            Активный пользователь - этот тот, кто зарегистрировался или сделал заказ или оплатил или посетил вебинар. Не активные пользователи НЕ учитываются. Если <br /> пользователь и зарегистрировался и посетил вебинар и оплатил, то он считается за 1 человека.
                        </p>
                        <UsersTable />
                    </>
            }
        </div>
    );
};

export default BalanceTab;
